import { motion } from "framer-motion";
import { ShieldCheck, MessageSquareText, Instagram } from "lucide-react";

// Honest, factual trust signal about our Meta/Instagram API integration.
// Do NOT change this copy to claim "Meta Verified" (that's a paid personal/
// business profile subscription, unrelated to app API permissions) or
// "Meta Platform Partner" (that's Meta's own vetted partner directory,
// which we are not part of). Keep this scoped to what's actually true:
// which Graph API permissions our app has been approved for.
const permissions = [
  { label: "Catalog access", approved: true },
  { label: "Comments", approved: true },
  { label: "DMs", approved: true },
];

// One connector line with a dot travelling along it. `delay` offsets the dot
// so the two lines look like a relay (Instagram -> API, then API -> Checkout DM).
// Layout (vertical on phones, horizontal from 640px) lives in App.css
// (.flow-wrap / .flow-node / .flow-line / .flow-dot).
function FlowLine({ delay = "0s" }) {
  return (
    <div className="flow-line bg-ink/15">
      <span
        className="flow-dot rounded-full bg-hotpink shadow-[0_0_10px_rgba(255,51,102,0.7)]"
        style={{ animationDelay: delay }}
      />
    </div>
  );
}

function FlowLabel({ children }) {
  return (
    <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.15em] text-ink/50">
      {children}
    </span>
  );
}

export default function MetaTrustStrip() {
  return (
    <section className="px-6 pb-16 md:px-12 md:pb-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mx-auto max-w-4xl rounded-3xl border border-ink/10 bg-lilac/30 px-6 py-5"
      >
        <div className="flex flex-col items-center gap-4 text-center md:flex-row md:justify-between md:text-left">
          <div className="flex items-center justify-center gap-3">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-white text-ink shadow-sm">
              <Instagram size={18} strokeWidth={2.4} />
            </span>
            <p className="text-left text-sm font-bold leading-snug text-ink md:text-base">
              Built on Instagram's official Graph API
              <span className="block text-xs font-normal text-ink/60">
                Formal App Review — not an unofficial workaround
              </span>
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 md:justify-end">
            {permissions.map((p, i) => (
              <motion.span
                key={p.label}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25 + i * 0.12, type: "spring", stiffness: 300, damping: 18 }}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold text-ink ${
                  p.approved ? "bg-mint" : "bg-peach"
                }`}
              >
                {p.approved ? (
                  <ShieldCheck size={13} />
                ) : (
                  <MessageSquareText size={13} />
                )}
                {p.label}
              </motion.span>
            ))}
          </div>
        </div>

        {/* Animated flow: Instagram -> Graph API -> Checkout DM
            Phones: vertical timeline. 640px and up: horizontal row. */}
        <div
          className="flow-wrap mt-5 border-t border-ink/10 pt-5"
          aria-label="Your Instagram connects to Checkout DM through Instagram's official Graph API"
          role="img"
        >
          <div className="flow-node">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-white shadow-sm">
              <Instagram size={20} strokeWidth={2.4} />
            </span>
            <FlowLabel>Instagram</FlowLabel>
          </div>

          <FlowLine />

          <div className="flow-node">
            <span className="grid h-11 flex-none place-items-center rounded-2xl bg-mint px-3 text-xs font-bold text-ink shadow-sm">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <ShieldCheck size={14} /> Graph API
              </span>
            </span>
            <FlowLabel>Official</FlowLabel>
          </div>

          <FlowLine delay="1.2s" />

          <div className="flow-node">
            <img src="/logo.png" alt="" className="h-11 w-11 flex-none rounded-2xl shadow-sm" />
            <FlowLabel>Checkout DM</FlowLabel>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
